import { z } from 'zod';
import { VersionType } from '@wix/ambassador-devcenter-v1-share-url/types';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import {
  AppGroup,
  IncompleteSetupReason,
} from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';

export const createAppSchema = z.object({
  app: z.object({
    id: z.string(),
  }),
});

export const createOAuthAppSchema = z.object({
  oAuthApp: z.object({
    id: z.string(),
  }),
});

const topologySchema = z
  .object({
    compId: z.string(),
    compName: z.string(),
    compType: z.literal(ComponentType.TOPOLOGY),
    compData: z.object({
      topology: z.object({
        topology: z.object({
          backendApiUrl: z.string().min(1),
        }),
      }),
    }),
  })
  .passthrough();

export type TopologyComponent = z.TypeOf<typeof topologySchema>;

const componentsSchema = z.array(
  z.discriminatedUnion('compType', [
    topologySchema,
    ...Object.values(ComponentType)
      .filter((type) => type !== ComponentType.TOPOLOGY)
      .map((type) => {
        return z
          .object({
            compId: z.string(),
            compName: z.string().optional(),
            compData: z.unknown(),
            compType: z.literal(type),
          })
          .passthrough();
      }),
  ])
);

export type AppComponents = z.TypeOf<typeof componentsSchema>;

export const appByVersionSchema = z.object({
  appId: z.string(),
  data: z.object({
    components: componentsSchema,
  }),
});

const appSecretsSchema = z.object({
  appSecret: z.string(),
  webhookPublicKey: z.string(),
});

export type AppSecrets = z.TypeOf<typeof appSecretsSchema>;

const appSchema = z.object({
  id: z.string(),
  name: z.string(),
  namespace: z.string().optional(),
  codeIdentifier: z.string().optional(),
});

export const getAppSchema = z.object({
  app: appSchema,
});

export const getAppWithSecretsSchema = z.object({
  app: appSchema.extend({
    appSecrets: appSecretsSchema,
  }),
});

const developmentSiteSchema = z
  .object({
    id: z.string(),
    siteName: z.string(),
  })
  // 'devSite' is missing displayName and viewUrl but are always created with an empty viewUrl (site not published) and a display name based on site name
  .transform(({ id, siteName }) => ({
    id,
    displayName: siteName.replaceAll('-', ' '),
    viewUrl: '',
  }));

export const createDevelopmentSiteSchema = z.object({
  developmentSite: developmentSiteSchema,
});

export const getDevelopmentSitesSchema = z.object({
  developmentSites: z.array(developmentSiteSchema),
});

export const getDevelopmentSitesLimitSchema = z.object({
  limit: z.number(),
});

const developerAppSchema = z.object({
  id: z.string(),
  appData: z.object({
    appName: z.string(),
  }),
});

export const getDeveloperAppsSchema = z.object({
  myApps: z.array(developerAppSchema),
});

export type DeveloperApp = z.TypeOf<typeof developerAppSchema>;

const requiredApp = z.object({ id: z.string(), name: z.string() });

export type App = z.TypeOf<typeof requiredApp>;

export const queryAppSchema = z.object({
  apps: z.array(requiredApp),
});

export const marketListingSchema = z.object({
  id: z.string(),
  appId: z.string(),
  revision: z.string(),
  languageCode: z.string(),
  installationRequirement: z
    .object({
      requiredApps: z.array(
        z.object({
          id: z.string(),
          slug: z.string(),
        })
      ),
    })
    .optional(),
});

export const managedAppsSchema = z.object({
  managedApps: z.array(
    z.object({
      appId: z.string(),
      installationDetails: z.object({
        group: z.nativeEnum(AppGroup),
        incompleteSetupReason: z.nativeEnum(IncompleteSetupReason),
      }),
    })
  ),
});

const cliCLoneDataSchema = z.object({
  repositoryUrl: z.string(),
  pathInRepository: z.string().optional(),
});

const appTemplateSchema = z.object({
  id: z.string(),
  title: z.string(),
  subtitle: z.string(),
  developmentTools: z.array(z.nativeEnum(DevelopmentTool)),
  cliCloneData: cliCLoneDataSchema.optional(),
});

export const queryAppTemplateSchema = z.object({
  appTemplates: z.array(appTemplateSchema),
});

export const cliAppTemplateSchema = appTemplateSchema.extend({
  cliCloneData: cliCLoneDataSchema,
});

export type CliAppTemplate = z.TypeOf<typeof cliAppTemplateSchema>;

const shareUrlSchema = z.object({
  versionType: z.nativeEnum(VersionType),
  installationUrl: z.string().url(),
  tinyInstallationUrl: z.string().url().optional(),
});

export const createShareUrlSchema = z.object({
  shareUrl: shareUrlSchema,
});

export const listShareUrlsSchema = z.object({
  shareUrls: z.array(shareUrlSchema),
});

export const setNamespaceSchema = z.object({
  namespace: z.string(),
});

export const setCodeIdentifierSchema = z.object({
  codeIdentifier: z.string(),
});

export const createCompanionAppSchema = z.object({
  companionApp: z.object({
    id: z.string(),
  }),
});

export const installAppSchema = z.object({
  appInstance: z.object({
    id: z.string(),
  }),
});

export {
  ComponentType,
  MonitoringType,
} from '@wix/ambassador-devcenter-apps-v1-app-old/types';
export { DevelopmentTool } from '@wix/ambassador-devcenter-apps-v1-app-template/types';
